// "Add to Home Screen": platform detection, the deferred Chrome install prompt, and the first standalone launch.
// Import this module eagerly (App.tsx) so `beforeinstallprompt`, which fires once early in the page load,
// is caught before the lazy onboarding screens load.
import { useEffect, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router';
import { track } from '@/lib/analytics';
import { useSession } from '@/lib/session';

type BeforeInstallPromptEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };

export type Platform = 'ios' | 'android' | 'desktop';

export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function platform(): Platform {
  if (isIOS()) return 'ios';
  if (/Android/i.test(navigator.userAgent)) return 'android';
  return 'desktop';
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return matchMedia('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
}

// ---------- deferred install prompt (Chrome, Edge, Samsung Internet) ----------
let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // we show our own button in onboarding and settings
    deferred = e as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    emit();
  });
}

/** The captured Chrome install prompt, or null (iOS, already installed, or the browser has none). */
export function useInstallPrompt() {
  const ev = useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    () => deferred,
    () => null,
  );
  return {
    available: !!ev,
    /** Shows the browser's install dialog; true when the parent accepted. */
    async prompt(): Promise<boolean> {
      if (!deferred) return false;
      const e = deferred;
      deferred = null;
      emit();
      await e.prompt();
      const { outcome } = await e.userChoice;
      return outcome === 'accepted';
    },
  };
}

// ---------- first launch from the Home Screen ----------
const INSTALLED_KEY = 'bp:installed';
const PUSH_OFFERED_KEY = 'bp:push-offered';

const read = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const write = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } };

export const markPushOffered = () => write(PUSH_OFFERED_KEY, '1');

/**
 * Mount once app-wide, inside <BrowserRouter> and <SessionProvider> (e.g. in AppShell):
 * - sends `app_installed` on the first standalone launch on this device (once, after sign-in);
 * - on that first launch, when onboarding is done but reminders were never offered in the installed app
 *   (iPhone: push is impossible in Safari), opens the reminders step /welcome/notify once.
 */
export function useInstallWatch(): void {
  const s = useSession();
  const nav = useNavigate();
  const ready = s.status === 'ready';
  const completed = !!s.me?.onboarding?.completedAt;

  useEffect(() => {
    if (!ready || !isStandalone()) return;
    if (!read(INSTALLED_KEY)) {
      write(INSTALLED_KEY, new Date().toISOString());
      track('app_installed', { platform: platform() }, 'onboarding');
    }
    const canAsk = 'Notification' in window && Notification.permission === 'default';
    if (completed && canAsk && !read(PUSH_OFFERED_KEY) && !location.pathname.startsWith('/welcome')) {
      markPushOffered();
      nav('/welcome/notify', { replace: true });
    }
  }, [ready, completed, nav]);
}

/** Same as useInstallWatch, as a component: `<InstallWatch />`. */
export function InstallWatch() {
  useInstallWatch();
  return null;
}
